import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { UserService } from '../services/user.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { User } from '../model/user';
import { getStoredToken, getStoredUser, setStoredToken } from '../helpers/user-storage.util';
import { AccountSideNavComponent } from '../account-side-nav/account-side-nav.component';
import { AuthenticationService } from '../services/authentication.service';
import { OptInDialogComponent } from '../sms/opt-in-dialog/opt-in-dialog.component';
import { OptOutDialogComponent } from '../sms/opt-out-dialog/opt-out-dialog.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    AccountSideNavComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    OptInDialogComponent,
    OptOutDialogComponent,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnDestroy {
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  user: User = {};
  outOfOfficeStartDate: Date | null = null;
  outOfOfficeEndDate: Date | null = null;
  editOutOfOfficeToggle: boolean = false;
  allNotifiedByEnabled: boolean = false;
  allInvitationNotificationsEnabled: boolean = false;
  allProposalNotifcationsEnabled: boolean = false;
  allActiveProjectNotificationsEnabled: boolean = false;
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  showOptInDialog: boolean = false;
  showOptOutDialog: boolean = false;
  isSubmittingPassword = false;
  isSavingSettings = false;

  // A password change still in flight when the user leaves must not write a token
  // for whoever is signed in by the time it answers.
  private destroy$ = new Subject<void>();

  protected newPassword = new FormGroup(
    {
      currentPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
      confirmNewPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
    },
    { validators: this.customPasswordMatching.bind(this) }
  );

  constructor(
    private userService: UserService,
    private authService: AuthenticationService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.user = getStoredUser();
    this.outOfOfficeStartDate = this.user.outOfOfficeStartDate
      ? new Date(this.user.outOfOfficeStartDate)
      : null;

    this.outOfOfficeEndDate = this.user.outOfOfficeEndDate
      ? new Date(this.user.outOfOfficeEndDate)
      : null;
    if (Boolean(this.outOfOfficeStartDate && this.outOfOfficeEndDate)) {
      this.editOutOfOfficeToggle = true;
    }

    this.allNotifiedByEnabled =
      (this.user.notifyByInApp &&
        this.user.notifyByEmail &&
        ((!this.user.phone && !this.user.phone2) || this.user.notifyBySms)) ||
      false;
    this.allInvitationNotificationsEnabled =
      (this.user.newInvitationNotification &&
        this.user.projectUpdateNotification &&
        this.user.cancelledInvitationNotification) ||
      false;
    this.allProposalNotifcationsEnabled =
      (this.user.awardedNotification &&
        this.user.closedNotification &&
        this.user.cancelledProposalNotification) ||
      false;
    this.allActiveProjectNotificationsEnabled =
      (this.user.revisionRequestedNotification &&
        this.user.completedNotification &&
        this.user.cancelledActiveProjectNotification) ||
      false;
  }

  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  toggleOptInDialog() {
    this.showOptInDialog = !this.showOptInDialog;
  }

  toggleOptOutDialog() {
    this.showOptOutDialog = !this.showOptOutDialog;
  }

  trimCurrentPassword() {
    let password = this.newPassword.get('currentPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        currentPassword: password.trim(),
      });
    }
  }

  trimNewPassword() {
    let password = this.newPassword.get('newPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        newPassword: password.trim(),
      });
    }
  }

  trimConfirmPassword() {
    let password = this.newPassword.get('confirmNewPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        confirmNewPassword: password.trim(),
      });
    }
  }

  public customPasswordMatching(
    control: AbstractControl
  ): ValidationErrors | null {
    const password = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmNewPassword')?.value;

    return password === confirmPassword
      ? null
      : { passwordMismatchError: true };
  }

  editOutOfOffice() {
    this.editOutOfOfficeToggle = !this.editOutOfOfficeToggle;
    if (this.editOutOfOfficeToggle === false) {
      this.outOfOfficeStartDate = null;
      this.outOfOfficeEndDate = null;
      this.handleUpdateUserOutOfOfficeDates();
    }
  }

  saveOutOfOffice() {
    // Check if both start and end dates are provided
    if (this.outOfOfficeStartDate && this.outOfOfficeEndDate) {
      // Validate that the end date is greater than the start date
      if (
        new Date(this.outOfOfficeEndDate) > new Date(this.outOfOfficeStartDate)
      ) {
        // Proceed with saving
        this.handleUpdateUserOutOfOfficeDates();
        // this.editOutOfOfficeToggle = !this.editOutOfOfficeToggle;
      } else {
        // If the end date is not greater than the start date, show an error
        this.toastr.info('End date must be after the start date.');
      }
    } else {
      // If either of the dates is missing
      this.toastr.info('Please select both start and end dates.');
    }
  }

  handleUpdateUserOutOfOfficeDates() {
    let userPayload = this.user;
    userPayload.outOfOfficeStartDate = this.outOfOfficeStartDate;
    userPayload.outOfOfficeEndDate = this.outOfOfficeEndDate;
    this.userService
      .updateUserOutOfOfficeDates(userPayload)
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'FAILURE') {
          this.toastr.error('An error occurred. Please try again.');
        }
        if (apiResponse.code !== 200) {
          this.toastr.error('An error occurred. Please try again.');
        } else if (apiResponse.code === 200) {
          this.toastr.success(apiResponse.message);
          this.user = { ...this.user, ...apiResponse.data };
          localStorage.setItem('user', JSON.stringify(this.user));
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveNewPassword() {
    if (this.newPassword.invalid) {
      this.newPassword.markAllAsTouched();
      return;
    } else if (this.newPassword.valid) {
      this.isSubmittingPassword = true;
      const startingSession = getStoredToken();
      this.authService
        .newPassword(
          this.user.email ?? '',
          this.newPassword.value.currentPassword ?? '',
          this.newPassword.value.newPassword ?? ''
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.isSubmittingPassword = false;
            if (data.status === 'SUCCESS') {
              // The change signs out every session issued before it, this one
              // included; the response carries a fresh token so this browser
              // stays signed in. Stored where login stores its token, but only
              // while the session that asked is still the one signed in: a
              // sign-out or another sign-in (in any tab) since then owns storage now.
              if (data.token && getStoredToken() === startingSession) {
                setStoredToken(data.token);
              }
              this.toastr.success(data.message);
              this.newPassword.reset({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: '',
              });
            } else {
              this.toastr.error(data.message);
            }
          },
          error: () => {
            this.isSubmittingPassword = false;
            this.toastr.error('An error occurred. Please try again.');
          }
        });
    }
  }

  saveNotifiedBy() {
    this.updateUser(this.user);
  }

  saveInvitationNotifications() {
    this.updateUser(this.user);
  }

  saveProposalNotifications() {
    this.updateUser(this.user);
  }

  saveActiveProjectNotifications() {
    this.updateUser(this.user);
  }

  handleSelectAllNotifiedBy() {
    this.user = {
      ...this.user,
      notifyByEmail: this.allNotifiedByEnabled,
      notifyByInApp: this.allNotifiedByEnabled,
      notifyBySms:
        ((this.user.phone || this.user.phone2) && this.allNotifiedByEnabled) ||
        false,
    };
  }

  handleNotifiedByChange() {
    this.allNotifiedByEnabled =
      (this.user.notifyByInApp &&
        this.user.notifyByEmail &&
        (this.user.phone || this.user.phone2) &&
        this.user.notifyBySms) ||
      false;
  }

  optInToSms(smsUser: any) {
    this.userService
      .optInUserToSmsNotification({ ...smsUser, notifyBySms: true })
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'SUCCESS') {
          this.toastr.success(apiResponse.message);
        } else {
          this.toastr.error(apiResponse.message);
        }
        this.user = { ...this.user, ...apiResponse.data };
        localStorage.setItem('user', JSON.stringify(this.user));
      });
    this.toggleOptInDialog();
  }

  optOutFromSms(smsUser: any) {
    this.userService
      .optOutUserFromSmsNotification(smsUser)
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'SUCCESS') {
          this.toastr.success(apiResponse.message);
        } else {
          this.toastr.error(apiResponse.message);
        }
        this.user = { ...this.user, ...apiResponse.data };
        localStorage.setItem('user', JSON.stringify(this.user));
      });
    this.toggleOptOutDialog();
  }

  handleSelectAllInvitationNotifications() {
    this.user = {
      ...this.user,
      newInvitationNotification: this.allInvitationNotificationsEnabled,
      projectUpdateNotification: this.allInvitationNotificationsEnabled,
      cancelledInvitationNotification: this.allInvitationNotificationsEnabled,
    };
  }

  handleInvitationNotificationChange() {
    this.allInvitationNotificationsEnabled =
      (this.user.newInvitationNotification &&
        this.user.projectUpdateNotification &&
        this.user.cancelledInvitationNotification) ||
      false;
  }

  handleSelectAllProposalNotifications() {
    this.user = {
      ...this.user,
      awardedNotification: this.allProposalNotifcationsEnabled,
      closedNotification: this.allProposalNotifcationsEnabled,
      cancelledProposalNotification: this.allProposalNotifcationsEnabled,
    };
  }

  handleProposalNotificationChange() {
    this.allProposalNotifcationsEnabled =
      (this.user.awardedNotification &&
        this.user.closedNotification &&
        this.user.cancelledProposalNotification) ||
      false;
  }

  handleSelectAllActiveProjectNotifications() {
    this.user = {
      ...this.user,
      revisionRequestedNotification: this.allActiveProjectNotificationsEnabled,
      completedNotification: this.allActiveProjectNotificationsEnabled,
      cancelledActiveProjectNotification:
        this.allActiveProjectNotificationsEnabled,
    };
  }

  handleActiveProjectNotificationChange() {
    this.allActiveProjectNotificationsEnabled =
      (this.user.revisionRequestedNotification &&
        this.user.completedNotification &&
        this.user.cancelledActiveProjectNotification) ||
      false;
  }

  private updateUser(user: User) {
    this.isSavingSettings = true;
    this.userService.updateUser(user).subscribe({
      next: (response: any) => {
        this.isSavingSettings = false;
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.toastr.success(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      },
      error: () => {
        this.isSavingSettings = false;
        this.toastr.error('An error occurred. Please try again.');
      }
    });
  }
}
